import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Token } from '../api/types';
import { dateTime } from '../lib/format';
import { useApp } from '../stores/app';

export function Tokens() {
  const { tokens, refreshTokens, user } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ id: string; value: string; title: string } | null>(null);
  const [form, setForm] = useState({ id: '', name: '', description: '', maxSessions: 0, maxStreams: 0, rateLimitRpm: 0 });

  useEffect(() => {
    void refreshTokens();
  }, [refreshTokens]);

  const run = async (fn: () => Promise<void>) => {
    try {
      await fn();
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
    await refreshTokens();
  };

  const create = (event: React.FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await api.createToken({
        ...(form.id ? { id: form.id } : {}),
        name: form.name,
        description: form.description || undefined,
        maxSessions: Number(form.maxSessions) || 0,
        maxStreams: Number(form.maxStreams) || 0,
        rateLimitRpm: Number(form.rateLimitRpm) || 0,
      });
      setSecret({ id: result.token.id, value: result.secret, title: 'Секрет нового токена' });
      setForm({ id: '', name: '', description: '', maxSessions: 0, maxStreams: 0, rateLimitRpm: 0 });
    });
  };

  const togglePause = (token: Token) =>
    run(async () => {
      await api.updateToken(token.id, { status: token.status === 'active' ? 'paused' : 'active' });
    });

  const rotate = (token: Token) =>
    run(async () => {
      const result = await api.rotateToken(token.id);
      setSecret({ id: token.id, value: result.secret, title: `Новый секрет токена ${token.id}` });
    });

  const isAdmin = user?.role === 'admin';

  return (
    <>
      <div className="topbar">
        <h1>Токены</h1>
      </div>

      {error && <div className="panel error-text">{error}</div>}

      {secret && (
        <div className="panel" style={{ borderColor: 'var(--warn)' }}>
          <h2>{secret.title}</h2>
          <p className="warn-text">
            Секрет показывается один раз. Пропишите его в справочнике рестримеров Мажордома вместе с идентификатором
            токена <code>{secret.id}</code> — восстановить значение потом будет нельзя, только выпустить новое.
          </p>
          <div className="copybox">
            <input readOnly value={secret.value} onFocus={(e) => e.currentTarget.select()} />
            <button className="shrink" onClick={() => void navigator.clipboard.writeText(secret.value)}>
              Копировать
            </button>
            <button className="shrink" onClick={() => setSecret(null)}>
              Скрыть
            </button>
          </div>
        </div>
      )}

      {isAdmin && (
        <form className="panel" onSubmit={create}>
          <h2>Новый токен</h2>
          <div className="row">
            <div className="field">
              <label>Название</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Мажордом iOS" />
            </div>
            <div className="field">
              <label>Идентификатор</label>
              <input
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                placeholder="mj-ios-01 (необязательно)"
              />
              <div className="hint">Попадёт в URL и в логи. Пусто — сгенерируем.</div>
            </div>
          </div>
          <div className="field">
            <label>Описание</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row">
            <div className="field">
              <label>Макс. сессий</label>
              <input type="number" min={0} value={form.maxSessions} onChange={(e) => setForm({ ...form, maxSessions: Number(e.target.value) })} />
            </div>
            <div className="field">
              <label>Макс. потоков</label>
              <input type="number" min={0} value={form.maxStreams} onChange={(e) => setForm({ ...form, maxStreams: Number(e.target.value) })} />
            </div>
            <div className="field">
              <label>Лимит запросов в минуту</label>
              <input type="number" min={0} value={form.rateLimitRpm} onChange={(e) => setForm({ ...form, rateLimitRpm: Number(e.target.value) })} />
            </div>
            <div className="field shrink">
              <button className="primary" type="submit">Создать</button>
            </div>
          </div>
          <p className="hint">0 в любом из лимитов означает «без ограничения».</p>
        </form>
      )}

      <div className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Токен</th>
                <th>Статус</th>
                <th>Лимиты</th>
                <th>Создан</th>
                <th>Последнее обращение</th>
                <th>Отзыв ссылок</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">Токенов нет.</td>
                </tr>
              )}
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.name}</strong>
                    <div className="muted mono">{t.id}</div>
                    {t.description && <div className="muted">{t.description}</div>}
                  </td>
                  <td>
                    <span className={`badge ${t.status}`}>{t.status === 'active' ? 'активен' : 'приостановлен'}</span>
                    {t.has_prev_secret && (
                      <div className="warn-text" style={{ fontSize: 11 }}>
                        есть предыдущий секрет
                      </div>
                    )}
                  </td>
                  <td className="muted">
                    сессий: {t.max_sessions || '∞'}
                    <br />
                    потоков: {t.max_streams || '∞'}
                    <br />
                    rpm: {t.rate_limit_rpm || '∞'}
                  </td>
                  <td className="muted">{dateTime(t.created_at)}</td>
                  <td className="muted">{dateTime(t.last_used_at)}</td>
                  <td className="muted">{t.links_valid_from ? dateTime(t.links_valid_from) : 'не было'}</td>
                  <td>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="small" onClick={() => void togglePause(t)}>
                          {t.status === 'active' ? 'Приостановить' : 'Возобновить'}
                        </button>
                        <button className="small" onClick={() => void rotate(t)} title="Новый секрет; прежний остаётся вторым ключом">
                          Ротировать
                        </button>
                        <button
                          className="small"
                          onClick={() => void run(async () => void (await api.revokeLinks(t.id)))}
                          title="Все ранее выпущенные конверты перестают работать; Мажордом выдаст новые"
                        >
                          Отозвать ссылки
                        </button>
                        {t.has_prev_secret && (
                          <button
                            className="small danger"
                            onClick={() => void run(async () => void (await api.dropPrevSecret(t.id)))}
                            title="Жёстко обрывает ссылки, выпущенные до ротации"
                          >
                            Убрать прежний секрет
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
